import React, {useEffect, useState} from 'react';
import {Box, Stack, Typography} from '@mui/material';
import {
    C71VO,
    formatAmount,
    getC71VO,
    overallFinalMateriality,
} from '@/services/auditService/materialityService';

export type FsMaterialityStatement = 'BS' | 'PL';

export interface FsMaterialityHeaderProps {
    companyId?: number | null;
    annualId?: number | null;
    /** BS → Gross assets materiality; PL → Turnover materiality (C7.1 Initial assessment). */
    statement: FsMaterialityStatement;
}

/**
 * Shows C7.1 materiality levels in the header area to the right of BS / PL titles.
 */
export const FsMaterialityHeader: React.FC<FsMaterialityHeaderProps> = ({
    companyId,
    annualId,
    statement,
}) => {
    const [data, setData] = useState<C71VO | null>(null);

    useEffect(() => {
        let cancelled = false;
        const cid = Number(companyId) || 0;
        const aid = Number(annualId) || 0;
        if (!cid || !aid) {
            setData(null);
            return;
        }
        (async () => {
            try {
                const vo = await getC71VO(cid, aid);
                if (!cancelled) setData(vo);
            } catch {
                if (!cancelled) setData(null);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [companyId, annualId]);

    if (!data) return null;

    const overallInitial = Number(data.thisYear ?? 0) || 0;
    const overallFinal = overallFinalMateriality(data);
    const statementInitial = statement === 'BS'
        ? (Number(data.grossMatLevel ?? 0) || 0)
        : (Number(data.turnMatLevel ?? 0) || 0);
    const statementLabel = statement === 'BS' ? 'Gross assets' : 'Turnover';
    // PL also surfaces Adjusted profit/(loss) Initial materiality level (circled on C7.1)
    const adjInitial = statement === 'PL' ? (Number(data.adjMatelityLevel ?? 0) || 0) : 0;

    if (!overallInitial && !overallFinal && !statementInitial && !adjInitial) {
        return null;
    }

    const Row = ({label, value}: { label: string; value: number }) => (
        <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="baseline">
            <Typography variant="caption" color="text.secondary" sx={{whiteSpace: 'nowrap'}}>
                {label}
            </Typography>
            <Typography variant="body2" fontWeight={700} sx={{fontVariantNumeric: 'tabular-nums'}}>
                {formatAmount(value) || '-'}
            </Typography>
        </Stack>
    );

    return (
        <Box
            sx={{
                minWidth: 200,
                maxWidth: 280,
                px: 1.5,
                py: 1,
                borderRadius: 1,
                bgcolor: 'action.hover',
                alignSelf: 'flex-start',
            }}
        >
            <Typography variant="subtitle2" fontWeight={700} mb={0.5}>
                Materiality level
            </Typography>
            {overallInitial !== 0 && <Row label="Overall (Initial)" value={overallInitial}/>}
            {overallFinal !== 0 && <Row label="Overall (Final)" value={overallFinal}/>}
            {statementInitial !== 0 && <Row label={statementLabel} value={statementInitial}/>}
            {adjInitial !== 0 && <Row label="Adj. profit/(loss)" value={adjInitial}/>}
        </Box>
    );
};

export default FsMaterialityHeader;
